import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

/**
 * ABOUT LIKEKIND PAGE — LOCKED COPY (PRE-DEPLOY)
 * 
 * Opens when logged-out user clicks "Profile" or from Settings > About
 * Contains: Primary welcome copy, Vision & intent, Safety explanation
 * 
 * DO NOT EDIT THIS COPY — Founder-approved final version
 */

export default function FounderInfo() {
  const navigate = useNavigate();
  const characterImg = "https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png";

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="mr-2"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>About LikeKind</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl mx-auto mb-4 overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
            <img src={characterImg} alt="LikeKind" className="w-20 h-20 object-cover" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            LikeKind
          </h1>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            A place to connect without being pushed
          </p>
        </div>

        {/* PRIMARY WELCOME COPY — LIKEKIND LOCKED (DO NOT EDIT) */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            backgroundColor: 'var(--fy-surface-muted)', 
            border: '1px solid var(--fy-border-subtle)'
          }}
        >
          <div className="space-y-4 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <p className="text-base" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind is a place for people who want to be present — with their communities, their interests, and their time.
            </p>
            <p>
              This isn't a feed designed to pull you in or push you around.
            </p>
            <p>
              It's a space to notice what's happening near you, connect intentionally, and build things together — calmly, openly, and on purpose.
            </p>
            <p>
              Your Base Camp page is your home base. It shows what you're part of, what's coming up, and what you might want to do next.
            </p>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Everything here is opt-in.
            </p>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Nothing here is ranked, boosted, or manipulated.
            </p>
            <p>
              Start where you are. See what's happening. Join when it feels right.
            </p>
          </div>
        </div>

        {/* What LikeKind Is */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            backgroundColor: 'var(--fy-surface-muted)', 
            border: '1px solid var(--fy-border-subtle)'
          }}
        >
          <h2 className="font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            What LikeKind Is
          </h2>
          <ul className="space-y-3 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-accent-positive)' }}>✓</span>
              <span>A place to share moments with people you know</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-accent-positive)' }}>✓</span>
              <span>Built around activities, not feeds</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-accent-positive)' }}>✓</span>
              <span>Private by structure, not by setting</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-accent-positive)' }}>✓</span>
              <span>Calm, quiet, and intentionally slow</span>
            </li>
          </ul>
        </div>

        {/* What LikeKind Is Not */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            backgroundColor: 'var(--fy-surface-muted)', 
            border: '1px solid var(--fy-border-subtle)'
          }}
        >
          <h2 className="font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            What LikeKind Is Not
          </h2>
          <ul className="space-y-3 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-text-disabled)' }}>✗</span>
              <span>No algorithmic feeds</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-text-disabled)' }}>✗</span>
              <span>No likes, followers, or engagement metrics</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-text-disabled)' }}>✗</span>
              <span>No public discovery of people</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: 'var(--fy-text-disabled)' }}>✗</span>
              <span>No ads or behavioral tracking</span>
            </li>
          </ul>
        </div>

        {/* Safety Explanation - CRITICAL */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            backgroundColor: 'var(--fy-background-secondary)', 
            border: '2px solid var(--fy-border-subtle)'
          }}
        >
          <h2 className="font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Privacy is the Structure
          </h2>
          <div className="space-y-4 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Profiles are only visible to people you are connected with.
            </p>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              There are no public profiles on LikeKind.
            </p>
            <p>
              You cannot be browsed, discovered, or found by strangers. Your moments are shared only with your circle — the people you choose to connect with.
            </p>
            <p>
              This is not a privacy setting you can turn on or off. It is how LikeKind works.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => navigate('/auth')}
            className="px-8 h-12 rounded-full font-semibold"
            style={{ 
              backgroundColor: 'var(--fy-accent-positive)', 
              color: 'var(--fy-background-primary)' 
            }}
          >
            Create Profile
          </Button>
          <p className="text-xs mt-3" style={{ color: 'var(--fy-text-disabled)' }}>
            Or continue browsing without signing in
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
